/* @moldura padrao */
/**
 * fornecedores.tsx — A lista de fornecedores de cada loja (migration 120, ficha #42).
 *
 * É aqui que se resolve a pendência "fornecedor sugerido": quem cadastrou no
 * Tiny liga a sugestão ao contato de lá, e as compras que a usaram herdam o
 * código. Sugeridos aparecem primeiro — são os que pedem ação.
 */
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { useAuth } from '@/auth/auth-context';
import { api, ApiError } from '@/lib/api';
import { CustomMolduraPagina } from '@/components/CustomMolduraPagina';
import { CustomBuscaContatoTiny, type FornecedorLoja } from '@/components/CustomCamposFornecedor';

interface Loja {
  id: number;
  codigo: string | null;
  nome: string;
  nome_curto?: string | null;
}

export function OrdensCompraFornecedoresPage() {
  const { pode } = useAuth();
  const [lojas, setLojas] = useState<Loja[]>([]);
  const [lojaId, setLojaId] = useState<number | null>(null);
  const [lista, setLista] = useState<FornecedorLoja[]>([]);
  const [ligando, setLigando] = useState<FornecedorLoja | null>(null);

  useEffect(() => {
    api.get<Loja[]>('/lojas').then((ls) => {
      setLojas(ls);
      if (ls.length) setLojaId(ls[0].id);
    });
  }, []);

  async function carregar() {
    if (!lojaId) return;
    setLista(await api.get<FornecedorLoja[]>(`/custos-pedido/ordens-compra/fornecedores?loja_id=${lojaId}`));
  }

  useEffect(() => {
    carregar().catch(() => setLista([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lojaId]);

  async function ligar(contatoTiny: string) {
    if (!ligando) return;
    try {
      await api.post(`/custos-pedido/ordens-compra/fornecedores/${ligando.id}/ligacao`, {
        contato_tiny: Number(contatoTiny),
      });
      toast.success(`${ligando.nome} ligado ao Tiny.`);
      setLigando(null);
      await carregar();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Não foi possível ligar.');
    }
  }

  const podeLigar = pode('custos-pedido.fornecedores.gerir');

  return (
    <CustomMolduraPagina
      titulo="Fornecedores"
      descricao="A lista de cada loja — cresce conforme as compras pedem"
      caminho={[{ rotulo: 'Ordens de compra', para: '/custos-pedido/ordens-compra' }]}
    >
      <ul className="nav nav-tabs adminuiux-tabs mb-3">
        {lojas.map((l) => (
          <li className="nav-item" key={l.id}>
            <button type="button" className={`nav-link ${lojaId === l.id ? 'active' : ''}`} onClick={() => setLojaId(l.id)}>
              {l.nome_curto || l.nome}
            </button>
          </li>
        ))}
      </ul>
      <div className="card adminuiux-card">
        <div className="card-body">
          <table className="table table-sm align-middle small mb-0 text-nowrap">
            <thead>
              <tr className="text-secondary text-uppercase">
                <th>Fornecedor</th>
                <th>CPF / CNPJ</th>
                <th>Código no Tiny</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {lista.map((f) => (
                <tr key={f.id}>
                  <td>{f.nome}</td>
                  <td>{f.cpf_cnpj ?? <span className="text-secondary">—</span>}</td>
                  <td className="font-monospace text-secondary">
                    {f.contato_tiny ?? <span className="badge casaa-selo-atencao">sugerido</span>}
                  </td>
                  <td className="text-end">
                    {f.cadastro === 'sugerido' && podeLigar && (
                      <button type="button" className="btn btn-sm btn-outline-theme" onClick={() => setLigando(f)}>
                        Ligar ao Tiny
                      </button>
                    )}
                  </td>
                </tr>
              ))}
              {lista.length === 0 && (
                <tr>
                  <td colSpan={4} className="py-4 text-center text-secondary">
                    Nenhum fornecedor na lista desta loja.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
      {lojaId && (
        <CustomBuscaContatoTiny
          lojaId={lojaId}
          aberto={!!ligando}
          titulo={`Ligar “${ligando?.nome ?? ''}” ao contato do Tiny`}
          termoInicial={ligando?.nome ?? ''}
          permiteSugerir={false}
          aoEscolher={(c) => ligar(c.contato_tiny)}
          aoFechar={() => setLigando(null)}
        />
      )}
    </CustomMolduraPagina>
  );
}
